import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";

import { uploadAssessmentTemplateImage } from "../../../../actions/assessments";
import { IThunkDispatch } from "../../../../store";
import ImageCrop from "../../../../components/ImageCrop/ImageCrop";
import { AttachmentInterface } from "../../../../interfaces/AttachmentInterface";

interface AssessmentTemplateImageUploadProps {
  category: string;
  onUploadSuccess?: (image: AttachmentInterface) => void;
  buttonText?: string;
  variant?: "contained" | "outlined" | "text";
  iconOnly?: boolean;
  disable?: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export function AssessmentTemplateImageUpload({
  category,
  onUploadSuccess,
  buttonText = "Add Image",
  variant = "contained",
  iconOnly = false,
  disable = false
}: AssessmentTemplateImageUploadProps): React.ReactElement {
  const dispatch = useDispatch<IThunkDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [temporaryImage, setTemporaryImage] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setTemporaryImage(reader.result as string);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async (croppedImage: Blob) => {
    setIsUploading(true);

    try {
      const result = await dispatch(uploadAssessmentTemplateImage(croppedImage, category));
      setCropperOpen(false);
      setTemporaryImage(null);
      if (onUploadSuccess && result) {
        onUploadSuccess(result);
      }
    } catch (err) {
      // Error is handled in action
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {iconOnly ? (
        <Tooltip title={buttonText}>
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || disable}
            size="small"
            sx={{ border: "1px solid", borderRadius: 1 }}
          >
            {isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant={variant}
          startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || disable}
          size="small"
        >
          {isUploading ? "Uploading..." : buttonText}
        </Button>
      )}

      <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} accept="image/*" />

      <ImageCrop
        image={temporaryImage}
        isOpen={cropperOpen}
        dynamicRatio={false}
        onCancel={() => {
          setCropperOpen(false);
          setTemporaryImage(null);
        }}
        onSave={handleUpload}
      />
    </Box>
  );
}
